import { InventoryAuthority } from '@qkindex/contracts';

export type RapidCaptureState =
  | 'pending'
  | 'submitting'
  | 'analysis-submitting'
  | 'confirming'
  | 'failed'
  | 'terminal';

export type RapidCaptureAuthority = InventoryAuthority & {
  membershipVersion: number;
  lifecycleVersion?: number;
};

export interface RapidCaptureRecord {
  serverAccepted?: boolean;
  serverRevision?: number;
  controlAction?: 'retry' | 'save';
  aiSettlement?: 'refunded' | 'reconciling';
  schemaVersion: 5;
  operationId: string;
  userId: string;
  collectionId: string;
  membershipVersion: number;
  lifecycleVersion?: number;
  compatibilityMode: RapidCaptureAuthority['compatibilityMode'];
  itemId: string;
  image: Blob;
  analyzeImage?: boolean;
  keywords: string;
  location: string;
  locationId?: string;
  qty: number;
  tagIds?: string[];
  state: RapidCaptureState;
  itemCreated: boolean;
  itemCreateAttempted?: boolean;
  attempts: number;
  createdAt: string;
  updatedAt: string;
  nextAttemptAt?: string;
  analysisOperationId?: string;
  acceptedAiCostVersion?: string;
  pendingAiCostVersion?: string;
  pendingAiCredits?: number;
  completion?: 'item' | 'analysis-complete';
  terminalReason?:
    | 'revoked'
    | 'membership-changed'
    | 'collection-deleting'
    | 'authority-invalid'
    | 'client-unsupported';
  aiSkipReason?: 'cost-unconfirmed' | 'cost-changed';
  lastError?: {
    category:
      | 'network'
      | 'authentication'
      | 'capacity'
      | 'quota'
      | 'permission'
      | 'conflict'
      | 'unknown';
    message: string;
    retryAfterSeconds?: number;
  };
}

export interface RapidCaptureDraft {
  image: Blob;
  analyzeImage?: boolean;
  keywords: string;
  location: string;
  locationId?: string;
  qty: number;
  tagIds?: string[];
}

export interface RapidCaptureStats {
  pending: number;
  active: number;
  analysisProcessing: number;
  confirming: number;
  failed: number;
  total: number;
  bytes: number;
}

export const sanitizeRapidCaptureTerminalRecord = (
  record: RapidCaptureRecord,
  terminalReason: NonNullable<RapidCaptureRecord['terminalReason']>,
  lastError: NonNullable<RapidCaptureRecord['lastError']>,
): RapidCaptureRecord => {
  // Unsupported local schemas may still contain a companion image. Access
  // invalidation must erase that private blob as well as the primary photo.
  const sanitized = { ...record } as RapidCaptureRecord & {
    labelScanImage?: Blob;
    ocrOperationId?: string;
    analysisResult?: unknown;
    analysisSourcePath?: string;
    skipSavedAnalysis?: boolean;
  };
  delete sanitized.labelScanImage;
  delete sanitized.ocrOperationId;
  delete sanitized.analysisResult;
  delete sanitized.analysisSourcePath;
  delete sanitized.skipSavedAnalysis;
  return {
    ...sanitized,
    state: 'terminal',
    controlAction: undefined,
    terminalReason,
    image: new Blob([], { type: 'application/octet-stream' }),
    analyzeImage: undefined,
    aiSettlement: undefined,
    analysisOperationId: undefined,
    keywords: '',
    tagIds: undefined,
    location: '',
    locationId: undefined,
    qty: 1,
    acceptedAiCostVersion: undefined,
    pendingAiCostVersion: undefined,
    pendingAiCredits: undefined,
    aiSkipReason: undefined,
    nextAttemptAt: undefined,
    lastError,
    updatedAt: new Date().toISOString(),
  };
};
